import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RefreshActions } from '../../state/refresh'
import { useRequests } from '../../requests/useRequests'
import { PRODUCT_CATEGORIES_TREE, CategoryItem } from '../../constants/productCategories'
import { Brand } from '../../types/brand'
import { Product } from '../../types/product'
import { PageBanner } from '../Banners/PageBanner'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'

const CategoryTreeItem = ({
  category,
  selectedCategory,
  onCategorySelect,
  level = 0
}: {
  category: CategoryItem
  selectedCategory: string
  onCategorySelect: (category: string) => void
  level?: number
}) => {
  const [expanded, setExpanded] = useState(false)

  const hasChildren = (category.children != null) && category.children.length > 0
  const isSelected = category.id === selectedCategory

  return (
    <div className='category-tree-item' style={{ marginLeft: `${level * 5}px` }}>
      <div className='category-header'>
        {hasChildren && (
          <span
            className={`expand-icon ${expanded ? 'expanded' : ''}`}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? '−' : '+'}
          </span>
        )}
        <div className='form-check d-flex align-items-center'>
          <input
            className='form-check-input'
            type='radio'
            name='category'
            id={`category-${category.id}`}
            checked={isSelected}
            onChange={() => onCategorySelect(category.id)}
          />
          <label className='form-check-label' htmlFor={`category-${category.id}`}>
            {category.label}
          </label>
          {isSelected && (
            <span
              className='deselect-icon ml-2'
              onClick={(e) => {
                e.stopPropagation()
                onCategorySelect(category.id)
              }}
              title='Odznacz kategorię'
              style={{ cursor: 'pointer' }}
            >
              ×
            </span>
          )}
        </div>
      </div>

      {hasChildren && expanded && (
        <div className='category-children'>
          {category.children!.map(child => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              selectedCategory={selectedCategory}
              onCategorySelect={onCategorySelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const ProductsZone: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requests = useRequests()
  const [searchParams, setSearchParams] = useSearchParams()

  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [brands, setBrands] = useState<Brand[]>([])
  const [fileFormats, setFileFormats] = useState<Array<{ id: string, label: string, count: number }>>([])
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([])
  const [adProducts, setAdProducts] = useState<Product[]>([])
  const [loadingFeatured, setLoadingFeatured] = useState<boolean>(true)
  const [loadingAd, setLoadingAd] = useState<boolean>(true)

  // Stany dla filtrów
  const [selectedBrand, setSelectedBrand] = useState<string>(searchParams.get('brand') || '')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get('category') || null)
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '')
  const [selectedFileFormat, setSelectedFileFormat] = useState<string>(searchParams.get('format') || '')

  // Stany dla paginacji
  const [currentPage, setCurrentPage] = useState<number>(parseInt(searchParams.get('page') || '1', 10))
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(12)

  // Dodanie selektora dla roli użytkownika z Redux
  const userRole = useSelector((state: any) => state.auth?.user?.role)
  const isSuperAdmin = userRole === 'superadmin'

  // Funkcja do wyświetlania powiadomień
  const showToast = (message: string) => {
    dispatch(
      RefreshActions.setToastMessage({
        message
      })
    )
  }

  // Pobieranie danych do filtrowania
  useEffect(() => {
    Promise.all([
      requests.getBrands({ limit: 12 }),
      requests.getBrandFileFormats()
    ])
      .then(([brandsRes, formatsRes]) => {
        setBrands(brandsRes.data.data || [])
        setFileFormats(Array.isArray(formatsRes.data) ? formatsRes.data : [])
      })
      .catch(error => {
        console.error('Błąd podczas pobierania danych do filtrowania:', error)
        showToast('Wystąpił błąd podczas pobierania danych filtrów')

        // Ustawienie pustych tablic dla innych danych
        setBrands([])
        setFileFormats([])
      })
  }, [])

  // Pobieranie produktów do reklam
  useEffect(() => {
    setLoadingFeatured(true)
    setLoadingAd(true)
    requests.getProducts({ limit: 20, sort: 'priority:desc' })
      .then(response => {
        const allProducts = response.data.data || []
        setFeaturedProducts(allProducts)
        setAdProducts(allProducts.slice(0, 12))
        setLoadingFeatured(false)
        setLoadingAd(false)
      })
      .catch(error => {
        console.error('Błąd podczas pobierania produktów do reklam:', error)
        setLoadingFeatured(false)
        setLoadingAd(false)
      })
  }, [requests])

  // Pobieranie produktów z filtrami
  useEffect(() => {
    setLoading(true)

    const brandId = selectedBrand ? parseInt(selectedBrand, 10) : undefined

    requests.getProducts({
      page: currentPage,
      limit: itemsPerPage,
      brandId: isNaN(brandId as number) ? undefined : brandId,
      category: selectedCategory || undefined,
      searchQuery: searchQuery || undefined,
      fileFormat: selectedFileFormat || undefined,
      sort: 'name:desc'
    })
      .then(response => {
        setProducts(response.data.data)
        setTotalCount(response.data.meta.total)
        setTotalPages(response.data.meta.pages)
        setLoading(false)
      })
      .catch(error => {
        console.error('Błąd podczas pobierania produktów:', error)
        showToast('Wystąpił błąd podczas pobierania produktów')
        setLoading(false)
      })
  }, [selectedBrand, selectedCategory, searchQuery, selectedFileFormat, currentPage, itemsPerPage])

  // Aktualizacja parametrów URL
  useEffect(() => {
    const params = new URLSearchParams()

    if (selectedBrand) params.set('brand', selectedBrand)
    if (selectedCategory) params.set('category', selectedCategory)
    if (searchQuery) params.set('search', searchQuery)
    if (selectedFileFormat) params.set('format', selectedFileFormat)
    if (currentPage > 1) params.set('page', currentPage.toString())

    setSearchParams(params)
  }, [selectedBrand, selectedCategory, searchQuery, selectedFileFormat, currentPage, setSearchParams])

  // Obsługa filtrów
  const handleBrandSelect = (brandId: string) => {
    setSelectedBrand((prev) => {
      return brandId === prev ? '' : brandId
    })
    setCurrentPage(1)
  }

  const handleCategorySelect = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null)
    } else {
      setSelectedCategory(categoryId)
    }
  }

  const handleFileFormatSelect = (format: string) => {
    setSelectedFileFormat(format === selectedFileFormat ? '' : format)
    setCurrentPage(1)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setSelectedBrand('')
    setSelectedCategory(null)
    setSelectedFileFormat('')
    setSearchQuery('')
    setCurrentPage(1)
  }

  // Obsługa paginacji
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Przekierowanie do szczegółów produktu
  const handleProductClick = (e: React.MouseEvent, id: number, idAdd: boolean) => {
    e.preventDefault()
    navigate(`/products/${id}${idAdd ? '?add=true' : ''}`)
  }

  // Funkcja renderująca hierarchiczną nazwę kategorii
  const renderCategoryName = (category: string) => {
    if (!category) return null

    const parts = category.split('/')
    if (parts.length <= 1) {
      return category
    }

    return (
      <span className='category-hierarchy'>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className='category-separator'> / </span>}
            <span className={`category-part ${index === parts.length - 1 ? 'category-leaf tw-text-white' : 'category-parent'}`}>
              {part}
            </span>
          </React.Fragment>
        ))}
      </span>
    )
  }

  // Funkcja do nawigacji do formularza dodawania produktu
  const handleAddProduct = () => {
    navigate('/products/add')
  }

  // Uproszczona funkcja do edycji produktu
  const handleEditProduct = (e: React.MouseEvent, productId: number) => {
    e.stopPropagation() // Zapobiega kliknięciu w kartę produktu
    navigate(`/products/${productId}/edit`)
  }

  return (
    <>
      <Header />
      <PageBanner title='Produkty' description='Przeglądaj i filtruj produkty z naszej bazy' />

      <section className='products-section tw-pt-16 md:tw-pt-20 lg:tw-pt-24 tw-pb-24 md:tw-pb-28 lg:tw-pb-36'>
        <div className='container'>
          <div className='section-header d-flex justify-content-between align-items-center mb-30'>

            {/* Przycisk dodawania widoczny tylko dla superadmina */}
            {isSuperAdmin && (
              <button
                className='btn btn__primary'
                onClick={handleAddProduct}
              >
                <i className='fas fa-plus mr-2' /> Dodaj produkt
              </button>
            )}
          </div>

          <div className='container-fluid'>
            <div className='tw-mb-6 d-lg-none'>
              <FeaturedTilesGrid
                products={featuredProducts}
                loading={loadingFeatured}
                onProductClick={(e, id) => handleProductClick(e, id, true)}
              />
            </div>
            <div className='row'>
              <div className='col-lg-3'>
                <div className='filter-sidebar'>
                  <div className='filter-header d-flex d-lg-none justify-content-between align-items-center mb-3'>
                    <h4 className='mb-0'>Filtry</h4>
                    <button className='btn btn-sm btn-outline-secondary'>
                      <i className='fas fa-times' />
                    </button>
                  </div>
                  <div className='filter-box mb-4'>
                    <h5 className='filter-title'>Wyszukaj</h5>
                    <div className='search-box'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Nazwa produktu...'
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                  <div className='filter-box mb-4'>
                    <h5 className='filter-title'>Marki</h5>
                    <div className='filter-list'>
                      {brands.length > 0
                        ? (
                            brands.map(brand => (
                              <div key={brand.id} className='filter-item'>
                                <div className='form-check'>
                                  <input
                                    type='radio'
                                    className='form-check-input'
                                    id={`brand-${brand.id}`}
                                    checked={selectedBrand === brand.id.toString()}
                                    onClick={() => handleBrandSelect(brand.id.toString())}
                                  />
                                  <label className='form-check-label' htmlFor={`brand-${brand.id}`}>
                                    {brand.name}
                                  </label>
                                </div>
                              </div>
                            ))
                          )
                        : (
                          <p>Ładowanie marek...</p>
                          )}
                    </div>
                  </div>
                  <div className='filter-box mb-4'>
                    <h5 className='filter-title'>Kategorie</h5>
                    <div className='filter-list category-tree'>
                      {PRODUCT_CATEGORIES_TREE.map(category => (
                        <CategoryTreeItem
                          key={category.id}
                          category={category}
                          selectedCategory={selectedCategory || ''}
                          onCategorySelect={handleCategorySelect}
                        />
                      ))}
                    </div>
                  </div>
                  <div className='filter-box mb-4'>
                    <h5 className='filter-title'>Formaty plików</h5>
                    <div className='filter-tags'>
                      {fileFormats.length > 0
                        ? (
                            fileFormats.map(format => (
                              <span
                                key={format.id}
                                className={`filter-tag ${selectedFileFormat === format.label ? 'active' : ''}`}
                                onClick={() => handleFileFormatSelect(format.label)}
                              >
                                {format.label} ({format.count})
                              </span>
                            ))
                          )
                        : (
                          <p>Ładowanie formatów...</p>
                          )}
                    </div>
                  </div>
                  {(selectedBrand || selectedCategory || selectedFileFormat || searchQuery) && (
                    <button
                      className={`
                                    tw-mb-4 tw-w-full tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                    tw-text-white
                                    tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                `}
                      onClick={clearAllFilters}
                    >
                      Wyczyść filtry
                    </button>
                  )}
                </div>
              </div>
              <div className='col-lg-9'>
                <div className='tw-mb-6 d-none d-lg-block'>
                  <FeaturedTilesGrid
                    products={featuredProducts}
                    loading={loadingFeatured}
                    onProductClick={(e, id) => handleProductClick(e, id, true)}
                  />
                </div>
                <div className='row'>
                  <div className='col-lg-9'>
                    {loading ? (
                      <div className='text-center py-5'>
                        <div className='spinner-border text-primary' role='status'>
                          <span className='sr-only'>Ładowanie...</span>
                        </div>
                      </div>
                    ) : (
                      <div className='row'>
                        {products.length > 0 ? (
                          products.map(product => (
                            <div key={product.id} className='col-sm-6 col-lg-4 mb-30'>
                              <div
                                className='tw-relative tw-aspect-[4/3] tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-group'
                                onClick={(e) => handleProductClick(e, product.id, false)}
                              >
                                {isSuperAdmin && (
                                  <div className='tw-absolute tw-top-2 tw-right-2 tw-z-10'>
                                    <button
                                      className='tw-bg-white tw-p-2 tw-rounded-full tw-shadow-md hover:tw-bg-slate-100 tw-transition-colors'
                                      onClick={(e) => handleEditProduct(e, product.id)}
                                      title='Edytuj produkt'
                                    >
                                      <i className='fas fa-edit tw-text-green' />
                                    </button>
                                  </div>
                                )}
                                <div className='tw-absolute tw-inset-0'>
                                  <img
                                    src={product.imagePresignedUrl || '/assets/images/product-placeholder.jpg'}
                                    alt={product.name}
                                    className='tw-w-full tw-h-full tw-object-cover'
                                    onError={(e) => {
                                      console.error('Błąd ładowania obrazka dla produktu:', product.id, product.name, product.imagePresignedUrl)
                                      e.currentTarget.src = '/assets/images/product-placeholder.jpg'
                                    }}
                                  />
                                </div>
                                <div className='tw-absolute tw-inset-0 tw-bg-black/70 tw-flex tw-items-center tw-justify-center tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100'>
                                  <div className='tw-text-center tw-p-4'>
                                    <h3 className='tw-text-white tw-text-lg tw-font-semibold tw-mb-2'>
                                      {product.name}
                                    </h3>
                                    <p className='tw-text-white tw-text-sm tw-mb-1'>
                                      Marka: {product.brand.name}
                                    </p>
                                    {/* <p className='tw-text-white tw-text-sm tw-mb-2'>
                                            {product.category && (
                                                <span className='tw-text-white'>
                                                                        {renderCategoryName(product.category)}
                                                                    </span>
                                            )}
                                          </p> */}
                                    <div className='tw-flex tw-flex-wrap tw-justify-center tw-gap-1'>
                                      {product.fileFormats.slice(0, 5).map((format, index) => (
                                        <span
                                            key={index}
                                            className={`tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-bg-white/20 tw-text-white ${format === selectedFileFormat ? 'tw-bg-green' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleFileFormatSelect(format)
                                            }}
                                          >
                                            {format}
                                          </span>
                                      ))}
                                      {product.fileFormats.length > 5 && (
                                        <span className='tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-bg-white/20 tw-text-white'>
                                            ...
                                          </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className='col-12 text-center py-5'>
                            <h4>Nie znaleziono produktów spełniających wybrane kryteria</h4>
                            <p>Spróbuj zmienić parametry wyszukiwania lub wyczyść filtry.</p>
                            <button
                              className={`
                                                    tw-mt-3 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                                    tw-text-white
                                                    tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                                `}
                              onClick={clearAllFilters}
                            >
                              Wyczyść filtry
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className='col-lg-3'>
                    <AdTileColumn
                      products={adProducts}
                      loading={loadingAd}
                      onProductClick={(e, id) => handleProductClick(e, id, true)}
                    />
                  </div>
                </div>
                {products.length > 0 && (
                  <div className='pagination-section mt-50 mb-30'>
                    <div className='row align-items-center'>
                      <div className='col-12'>
                        <div className='pagination-info-wrapper text-center mb-4'>
                          <div className='pagination-current-page'>
                            <span className='badge badge-pill badge-primary px-4 py-2 current-page-large'>
                              <span className='page-indicator'>Strona</span> <strong className='page-number'>{currentPage}</strong> <span className='page-indicator'>z</span> <strong className='page-number'>{totalPages}</strong>
                            </span>
                          </div>
                          <div className='pagination-total-info mt-2'>
                            Wyświetlanie <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> - <strong>{Math.min(currentPage * itemsPerPage, totalCount)}</strong> z <strong>{totalCount}</strong> produktów
                          </div>
                        </div>
                        {totalPages > 1 && (
                          <nav aria-label='Nawigacja strony produktów' className='product-pagination'>
                            <ul className='pagination pagination-lg justify-content-center'>
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <a
                                  className='page-link'
                                  href='#'
                                  onClick={(e) => {
                                    e.preventDefault()
                                    if (currentPage > 1) handlePageChange(currentPage - 1)
                                  }}
                                  aria-label='Poprzednia'
                                >
                                  <span aria-hidden='true'>&laquo;</span>
                                  <span className='sr-only'>Poprzednia</span>
                                </a>
                              </li>
                              {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(page =>
                                  page === 1 ||
                                          page === totalPages ||
                                          (page >= currentPage - 1 && page <= currentPage + 1)
                                )
                                .map((page, index, array) => {
                                  const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1
                                  const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== page + 1
                                  return (
                                    <React.Fragment key={page}>
                                      {showEllipsisBefore && (
                                          <li className='page-item disabled'>
                                            <span className='page-link'>...</span>
                                          </li>
                                        )}
                                      <li className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                          <a
                                            className='page-link'
                                            href='#'
                                            onClick={(e) => {
                                              e.preventDefault()
                                              handlePageChange(page)
                                            }}
                                          >
                                            {page}
                                          </a>
                                        </li>
                                      {showEllipsisAfter && (
                                          <li className='page-item disabled'>
                                            <span className='page-link'>...</span>
                                          </li>
                                        )}
                                    </React.Fragment>
                                  )
                                })}
                              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <a
                                  className='page-link'
                                  href='#'
                                  onClick={(e) => {
                                    e.preventDefault()
                                    if (currentPage < totalPages) handlePageChange(currentPage + 1)
                                  }}
                                  aria-label='Następna'
                                >
                                  <span aria-hidden='true'>&raquo;</span>
                                  <span className='sr-only'>Następna</span>
                                </a>
                              </li>
                            </ul>
                          </nav>
                        )}
                        <div className='items-per-page-selector-wrapper text-center mt-4'>
                          <div className='items-per-page-selector d-inline-block'>
                            <label className='mr-2'>Produktów na stronie:</label>
                            <select
                              className='form-control form-control-sm d-inline-block w-auto'
                              value={itemsPerPage}
                              onChange={(e) => {
                                const newItemsPerPage = parseInt(e.target.value, 10)
                                setItemsPerPage(newItemsPerPage)
                                setCurrentPage(1)
                              }}
                            >
                              <option value='12'>12</option>
                              <option value='24'>24</option>
                              <option value='48'>48</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default ProductsZone
const FeaturedTilesGrid: React.FC<{
  products: Product[]
  loading: boolean
  onProductClick: (e: React.MouseEvent, id: number) => void
}> = ({ products, loading, onProductClick }) => {
  if (loading) {
    return (
      <div className='tw-bg-slate-50 tw-p-6 tw-rounded-xl'>
        <div className='tw-flex tw-items-center tw-justify-center tw-h-40'>
          <div className='spinner-border text-primary' role='status'>
            <span className='sr-only'>Ładowanie...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='tw-bg-slate-50 tw-p-6 tw-rounded-xl'>
      <div className='tw-flex tw-items-center tw-justify-end tw-mb-4'>
        <span className='tw-text-xs tw-font-semibold tw-text-slate-400 tw-tracking-wider'>REKOMENDOWANE</span>
      </div>
      <div className='tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-6 tw-gap-4'>
        {products.map(product => (
          <a
            href={`/products/${product.id}?add=true`}
            key={product.id}
            className='tw-aspect-[4/3] tw-overflow-hidden tw-rounded-md tw-group tw-cursor-pointer tw-block'
            onClick={(e) => onProductClick(e, product.id)}
          >
            <img
              src={product.imagePresignedUrl || '/assets/images/product-placeholder.jpg'}
              alt={product.name}
              className='tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-105 tw-transition-transform tw-duration-300'
              onError={(e) => {
                e.currentTarget.src = '/assets/images/product-placeholder.jpg'
              }}
            />
          </a>
        ))}
      </div>
    </div>
  )
}

const AdTileColumn: React.FC<{
  products: Product[]
  loading: boolean
  onProductClick: (e: React.MouseEvent, id: number) => void
}> = ({ products, loading, onProductClick }) => {
  if (loading) {
    return (
      <div className='tw-bg-slate-50 tw-p-4 tw-rounded-lg'>
        <div className='tw-flex tw-items-center tw-justify-center tw-h-40'>
          <div className='spinner-border text-primary' role='status'>
            <span className='sr-only'>Ładowanie...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='tw-bg-slate-50 tw-p-4 tw-rounded-lg'>
      <div className='tw-flex tw-items-center tw-justify-end tw-mb-4'>
        <span className='tw-text-xs tw-font-semibold tw-text-slate-400 tw-tracking-wider'>REKOMENDOWANE</span>
      </div>

      <div className='tw-grid tw-grid-cols-2 tw-gap-3'>
        {products.map(product => (
          <a
            href={`/products/${product.id}?add=true`}
            key={product.id}
            className='tw-block tw-aspect-square tw-overflow-hidden tw-rounded-md tw-group tw-cursor-pointer'
            onClick={(e) => onProductClick(e, product.id)}
          >
            <img
              src={product.imagePresignedUrl || '/assets/images/product-placeholder.jpg'}
              alt={product.name}
              className='tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-105 tw-transition-transform tw-duration-300'
              onError={(e) => {
                e.currentTarget.src = '/assets/images/product-placeholder.jpg'
              }}
            />
          </a>
        ))}
      </div>
    </div>
  )
}
